import React, { useState, useEffect, useRef, ReactNode, FocusEvent, KeyboardEvent, FormEvent } from 'react'

import { useTextField } from 'react-aria'

import clsx from 'clsx';

import styleClasses from './TextInput.module.css';
import { Pressable } from '../Button';

interface TextInputProps {
  prefix?: ReactNode;
  suffix?: ReactNode;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  className?: string;
  color?: string;
  styled?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onCancel?: () => void;
  onConfirm?: () => Promise<void> | void;
  showConfirmIcon?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  [key: string]: any;
}

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {

    const inputRef = useRef<HTMLInputElement | null>(null);

    const {
        prefix, suffix, onFocus, onBlur, className, color, styled, isDisabled,
        // maxLength,
        value = "", onChange = (_: string) => { },

        onCancel, onConfirm,

        showConfirmIcon = true,

        onKeyDown,

        ...otherProps
    } = props;

    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (props.autoFocus && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus()
            }, 1)
        }
    }, [props.autoFocus, inputRef])

    // appearance:  outline, 

    const [localValue, setLocalValue] = useState(value);

    const [isComposing, setIsComposing] = useState(false);

    useEffect(() => {
        if (value != localValue) {
            setLocalValue(value)
        }
    }, [value])

    function doChange(v: string): void {
        setLocalValue(v)
        if (!isComposing) {
            onChange(v)
        }
    }
    useEffect(() => {
        if (!isComposing) {
            onChange(localValue)
        }
    }, [isComposing]);

    const { labelProps, inputProps, descriptionProps, errorMessageProps } = useTextField({
        value: localValue,
        onChange: (v) => {
            // if(maxLength && v.length > maxLength) {
            //     doChange(v.substring(0, maxLength))
            // } else {
            doChange(v);
            // }           
        },
        onFocus: (e: FocusEvent<Element>) => {
            if (onFocus) { onFocus(e as FocusEvent<HTMLInputElement>) }
            setFocused(true)
        },
        onBlur: (e: FocusEvent<Element>) => {
            if (onBlur) { onBlur(e as FocusEvent<HTMLInputElement>) }
            setFocused(false)
        },

        onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
            if (onKeyDown) {
                // 使用用户传入的：
                onKeyDown(e)
            } else {
                if (e.key === "Enter" && onConfirm) {
                    onConfirm()
                }
                if (e.key === "Escape" && onCancel) {
                    onCancel()
                }
            }
        },
        "aria-label": "输入框",
        isDisabled,
        ...otherProps
    }, inputRef);

    const icon = (e: ReactNode, className?: string) => (
        <div className={clsx(styleClasses["adornment"], className)}>
            {e}
        </div>
    )

    const iconButton = (e: ReactNode, className?: string, onPress?: () => void, isDisabled?: boolean) => {

        const inner = (
            <div className={clsx(
                styleClasses["adornment"], className, "rounded h-5 w-5 flex justify-center items-center",
                {
                    "!opacity-50": isDisabled,
                    "hover:bg-[--gray-4]": !isDisabled
                }
            )}>
                {e}
            </div>
        )

        if (isDisabled) {
            return inner;
        }

        return (
            <Pressable onPress={onPress}>
                {inner}
            </Pressable>
        )
    }

    const [isConfirming, setIsConfirming] = useState(false);

    return (
        <div className={clsx(
            styleClasses["lc-text-input"], className,
            styled ? styleClasses["styled"] : null,
            isDisabled ? styleClasses["disabled"] : null,
            "py-2 flex gap-0.5",
            !prefix ? "pl-2" : "pl-1",
            !suffix && !onCancel && !onConfirm ? "pr-2" : "pr-1",

        )} data-focused={focused} data-color={color}>
            {prefix ? icon(prefix, "prefix") : null}
            <input {...inputProps} ref={(r: HTMLInputElement | null) => {
                inputRef.current = r;
                if (ref) {
                    if (typeof (ref) === "function") {
                        ref(r)
                    } else {
                        ref.current = r;
                    }
                }


            }}
                onCompositionStart={(_: FormEvent) => {
                    setIsComposing(true)
                }}
                onCompositionEnd={(_: FormEvent) => {
                    setTimeout(() => {
                        setIsComposing(false)
                    }, 16);
                }}
            />
            {onCancel ? iconButton(<i className="bx bx-x" />, "suffix text-[--gray-11] ", () => onCancel(), isConfirming) : null}
            {onConfirm && showConfirmIcon ? iconButton(<i className="bx bx-check" />, "suffix text-[--grass-11] ", async () => {
                setIsConfirming(true);
                await onConfirm();
                setIsConfirming(false);
            }, !(value && value.trim().length > 0) || isConfirming) : null}
            {suffix ? icon(suffix, "suffix") : null}
        </div>
    )
})


export default TextInput; 